/**
 * audio/synth.ts — Low-level Web Audio Synthesis Primitives
 *
 * The building blocks that every sound in Hearth is made of.
 * No audio files. No samples. Just math, time, and silence shaped
 * into something that feels like a room breathing.
 *
 * These functions are stateless: they take a context, return a node.
 * Wiring, scheduling, and lifecycle management live in engine.ts.
 */

// ─── Noise Generation ─────────────────────────────────────────────────────────

const NOISE_BUFFER_SECONDS = 2; // seconds of noise to pre-generate (2s = inaudible loop seam)
const NOISE_SAMPLE_RATE_DIVISOR = 1; // use full sample rate

/**
 * Generate a looping noise source.
 *
 * white  — pure random: flat spectrum, maximum energy across all frequencies
 * pink   — -3dB/octave rolloff: sounds "natural", closest to what ears expect
 * brown  — -6dB/octave rolloff: deep, rumbling, like a distant engine or fire
 *
 * The returned node is already started (call .start() yourself if you want
 * deferred playback). Set .loop = true before connecting to keep it running.
 */
export function createNoise(
  ctx: AudioContext,
  type: "white" | "pink" | "brown"
): AudioBufferSourceNode {
  const sampleRate = ctx.sampleRate;
  const frameCount = Math.floor(sampleRate * NOISE_BUFFER_SECONDS * NOISE_SAMPLE_RATE_DIVISOR);
  const buffer = ctx.createBuffer(2, frameCount, sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel);

    if (type === "white") {
      // Pure random — every sample independent
      for (let i = 0; i < frameCount; i++) {
        data[i] = Math.random() * 2 - 1;
      }

    } else if (type === "pink") {
      // Paul Kellett's refined pink noise approximation
      // Each octave of pink noise is filtered from white — this does it in one pass
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < frameCount; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }

    } else {
      // Brown noise: integrated white noise (-6dB/octave)
      // Each sample is the previous sample plus a small random step,
      // then normalized so the signal doesn't drift to the rails.
      let lastOut = 0;
      for (let i = 0; i < frameCount; i++) {
        const white = Math.random() * 2 - 1;
        lastOut = (lastOut + 0.02 * white) / 1.02;
        data[i] = lastOut * 3.5; // boost back to useful amplitude
      }
    }
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  return source;
}

// ─── Reverb ───────────────────────────────────────────────────────────────────

/**
 * Generate a procedural convolution reverb.
 *
 * Produces a ConvolverNode whose impulse response is exponentially decaying
 * stereo noise with high-frequency rolloff over time (mimicking real rooms
 * where treble is absorbed faster than bass).
 *
 * duration  — length of the reverb tail in seconds (0.5 = tight; 2.5 = spacious)
 * decay     — how quickly the tail falls off (1 = linear; 3 = sharp)
 * lpDecay   — optional: high-frequency absorption rate (0 = none, 0.8 = strong)
 * predelay  — optional: early reflection gap in seconds (0.005 = small room)
 * tonalBias — optional: -1 (warm/dark) to +1 (bright). 0 = neutral.
 *
 * For Hearth's intimate domestic spaces: duration ≈ 0.8, decay ≈ 2.
 * For stone rooms: duration ≈ 1.4, decay ≈ 1.8, lpDecay ≈ 0.3 (bright)
 * For wood rooms: duration ≈ 0.6, decay ≈ 2.5, lpDecay ≈ 0.6 (warm)
 * For fabric-heavy rooms: duration ≈ 0.3, decay ≈ 3.0, lpDecay ≈ 0.9 (dead)
 */
export function createReverb(
  ctx: AudioContext,
  duration: number,
  decay: number,
  lpDecay: number = 0.4,
  predelay: number = 0.006,
  tonalBias: number = 0
): ConvolverNode {
  const sampleRate = ctx.sampleRate;
  const predelaySamples = Math.floor(sampleRate * Math.max(0, predelay));
  const tailLength = Math.max(1, Math.floor(sampleRate * Math.max(0.001, duration)));
  const totalLength = predelaySamples + tailLength;
  const impulse = ctx.createBuffer(2, totalLength, sampleRate);

  // Tonal bias affects the frequency rolloff starting point
  // Negative = warmer (more HF absorption), positive = brighter
  const effectiveLpDecay = Math.max(0, Math.min(1, lpDecay - tonalBias * 0.3));

  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);

    // Predelay gap (silence) — simulates initial delay before first reflection
    // (already zeros from buffer creation)

    // Early reflections — first few ms have discrete, stronger taps
    const earlyLen = Math.min(Math.floor(sampleRate * 0.02), tailLength);
    for (let i = 0; i < earlyLen; i++) {
      const t = i / tailLength;
      // Sparse taps (every ~1ms) with channel offset for stereo width
      if ((i + channel * 3) % Math.floor(sampleRate * 0.001) < 2) {
        data[predelaySamples + i] = (Math.random() * 2 - 1) * (1 - t * 0.5) * 0.6;
      }
    }

    // Late diffuse tail — filtered decaying noise
    let lpState = 0; // simple one-pole LP state for HF absorption
    for (let i = earlyLen; i < tailLength; i++) {
      const t = i / tailLength;
      // Exponential decay envelope
      const envelope = Math.pow(1 - t, decay);
      // High-frequency absorption: progressively filter more as t increases
      const lpCoeff = effectiveLpDecay * t; // 0 at start → lpDecay at end
      const white = Math.random() * 2 - 1;
      lpState = lpState + lpCoeff * (white - lpState);
      const sample = (white * (1 - lpCoeff) + lpState * lpCoeff);
      data[predelaySamples + i] = sample * envelope;
    }
  }

  const reverb = ctx.createConvolver();
  reverb.buffer = impulse;
  return reverb;
}

// ─── Filter Factories ─────────────────────────────────────────────────────────

/**
 * Low-pass filter: passes frequencies below `freq`, attenuates above.
 * Use to muffle, soften, or give things that "through a wall" quality.
 *
 * Q controls resonance at the cutoff — higher = more pronounced peak.
 * For room tone: Q = 0.7 (flat). For vinyl warmth: Q = 1.2.
 */
export function createLPF(
  ctx: AudioContext,
  freq: number,
  Q: number = 0.7
): BiquadFilterNode {
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = freq;
  filter.Q.value = Q;
  return filter;
}

/**
 * High-pass filter: passes frequencies above `freq`, attenuates below.
 * Use to strip rumble, isolate presence, or thin out a sound.
 *
 * For grass rustle: HPF at ~2000Hz cuts the body and leaves only air.
 */
export function createHPF(
  ctx: AudioContext,
  freq: number,
  Q: number = 0.7
): BiquadFilterNode {
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = freq;
  filter.Q.value = Q;
  return filter;
}

/**
 * Band-pass filter: passes a band of frequencies centered at `freq`.
 * Attenuates everything above and below.
 *
 * The character of a sound through a BPF changes dramatically with Q:
 *   Q=1  → wide, warm, blurry
 *   Q=5  → narrow, present, almost tonal
 *   Q=10 → almost a sine wave
 *
 * Fire crackle: BPF at 800Hz Q=2. Wood footstep: BPF at 400Hz Q=3.
 */
export function createBPF(
  ctx: AudioContext,
  freq: number,
  Q: number = 1
): BiquadFilterNode {
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = freq;
  filter.Q.value = Q;
  return filter;
}
